import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { AppShell } from "./components/AppShell";
import { OwnerAnnouncement } from "./components/OwnerAnnouncement";
import { BrandMark } from "./components/BrandMark";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { OfflineOutboxBanner } from "./components/OfflineOutboxBanner";
import { UpdateBanner } from "./components/UpdateBanner";
import { setOutboxOwner } from "./lib/offline-link";
import { clearLegacyPrivatePhotoCaches } from "./lib/private-cache";
import { trpc, type UserSession } from "./lib/trpc";
import { CatalogPage } from "./pages/CatalogPage";
import { ChatPage } from "./pages/ChatPage";
import { AcceptInvitePage } from "./pages/AcceptInvitePage";
import { AccessBlockedPage } from "./pages/AccessBlockedPage";
import { ConsolePage } from "./pages/ConsolePage";
import { ProfilePage } from "./pages/ProfilePage";
import { InterventionPage } from "./pages/InterventionPage";
import { InterventionsPage } from "./pages/InterventionsPage";
import { LoginPage } from "./pages/LoginPage";
import { PriceComparisonPage } from "./pages/PriceComparisonPage";
import { PurchaseOrderPage } from "./pages/PurchaseOrderPage";
import { PurchaseOrdersPage } from "./pages/PurchaseOrdersPage";
import { PurchaseReportPage } from "./pages/PurchaseReportPage";
import { PurchaseRequestsPage } from "./pages/PurchaseRequestsPage";
import { PoolsPage } from "./pages/PoolsPage";
import { PurchasingLayout } from "./pages/PurchasingLayout";
import { SitePage } from "./pages/SitePage";
import { SitesPage } from "./pages/SitesPage";
import { SupportPage } from "./pages/SupportPage";
import { SuppliersPage } from "./pages/SuppliersPage";
import { TeamPage } from "./pages/TeamPage";
import { TodayPage } from "./pages/TodayPage";
import { WeekPage } from "./pages/WeekPage";
import { WorkLogPage } from "./pages/WorkLogPage";

function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [location.pathname]);
  return null;
}

/**
 * L'avviso di versione nuova vive fuori dalle schermate, cosi vale anche sull'accesso
 * e su un'app rimasta aperta per giorni senza mai cambiare pagina.
 */
export function App() {
  return (
    <>
      <AppScreens />
      <UpdateBanner />
    </>
  );
}

function AppScreens() {
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  async function replaceSession(nextSession: UserSession | null) {
    await clearLegacyPrivatePhotoCaches();
    queryClient.clear();
    utils.auth.me.setData(undefined, nextSession);
  }
  const session = trpc.auth.me.useQuery(undefined, { retry: false });
  const login = trpc.auth.login.useMutation({ onSuccess: replaceSession });
  const logout = trpc.auth.logout.useMutation({ onSuccess: () => replaceSession(null) });
  const [loginError, setLoginError] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const acceptInvite = trpc.auth.acceptInvite.useMutation({ onSuccess: replaceSession });
  const location = useLocation();

  // La coda deve sapere di chi e prima che qualcuno scriva qualcosa: senza proprietario
  // un'azione fatta offline non saprebbe a quale azienda tornare al ritorno della rete.
  const companyId = session.data?.company.id ?? null;
  const userId = session.data?.user.id ?? null;
  useEffect(() => {
    setOutboxOwner(companyId && userId ? { companyId, userId } : null);
  }, [companyId, userId]);

  useEffect(() => {
    if (!session.data) return;
    const events = new EventSource("/api/events");
    events.addEventListener("update", (message) => {
      try {
        const event = JSON.parse((message as MessageEvent<string>).data) as { path?: string };
        const area = event.path?.split(".")[0];
        if (area === "interventions") void utils.interventions.invalidate();
        else if (area === "sites") void utils.sites.invalidate();
        else if (area === "team") void utils.team.invalidate();
        else if (area === "catalog") void utils.catalog.invalidate();
        else if (area === "clients") void utils.clients.invalidate();
        else if (area === "purchasing") void utils.purchasing.invalidate();
        else if (area === "suppliers") void utils.suppliers.invalidate();
        else if (area === "materials") void utils.materials.invalidate();
        else if (area === "modules") void utils.modules.invalidate();
        // Il programma settimanale genera interventi: chi guarda la settimana e chi guarda
        // gli elenchi devono vedere la stessa cosa senza ricaricare la pagina.
        else if (area === "pools") void utils.pools.invalidate();
        // Una misurazione chiude anche l'attività, quindi la scheda dell'intervento va riletta.
        else if (area === "measurements") {
          void utils.measurements.invalidate();
          void utils.interventions.invalidate();
        }
        // Una voce libera non tocca nessun intervento, ma cambia il registro di chi lo guarda.
        else if (area === "workLog") void utils.workLog.invalidate();
        // Un messaggio nuovo deve comparire senza ricaricare: e una conversazione, non un elenco.
        else if (area === "chat") void utils.chat.invalidate();
        else if (area === "scheduling") {
          void utils.scheduling.invalidate();
          void utils.interventions.invalidate();
        }
        // Le idee e segnalazioni non si leggono dentro l'app: non c'e nulla da aggiornare.
        // Senza questo ramo finivano nel caso generale, che rilegge anche la sessione di accesso.
        else if (area === "feedback") return;
        else void utils.invalidate();
      } catch {
        void utils.invalidate();
      }
    });
    return () => events.close();
  }, [session.data, utils]);

  // La console vive fuori dall'app: altro accesso, altro cookie, nessun menu aziendale.
  // Il controllo sta prima di tutto il resto, cosi non passa mai dalla schermata di accesso
  // dell'app chi sta andando in un'altra porta.
  if (location.pathname.startsWith("/console")) return <ConsolePage />;

  if (session.isLoading) return <main className="splash"><BrandMark /><span className="loader" /><p>Prepariamo il lavoro di oggi...</p></main>;
  if (!session.data && location.pathname === "/accetta-invito") {
    const token = new URLSearchParams(location.search).get("token") ?? "";
    return <AcceptInvitePage token={token} loading={acceptInvite.isPending} error={inviteError} onAccept={async (input) => {
      setInviteError(null);
      try { await acceptInvite.mutateAsync(input); }
      catch (error) { setInviteError(error instanceof Error ? error.message : "Invito non valido."); }
    }} />;
  }
  if (!session.data) return <LoginPage loading={login.isPending} error={loginError} onLogin={async (credentials) => {
    setLoginError(null);
    try { await login.mutateAsync(credentials); }
    catch (error) { setLoginError(error instanceof Error ? error.message : "Accesso non riuscito."); }
  }} />;

  // Accesso di assistenza: l'avviso sta sopra a tutto, comprese le schermate di porta
  // chiusa. Chi guarda i dati di un'altra azienda lo deve leggere da qualunque schermata.
  const avvisoAssistenza = <ImpersonationBanner session={session.data} />;

  // Prova finita o azienda sospesa: la sessione resta valida e la schermata spiega, invece
  // di buttare fuori senza dire niente. I dati non si toccano.
  if (session.data.company.access.blocked) {
    return <>{avvisoAssistenza}<AccessBlockedPage session={session.data} onLogout={() => logout.mutate()} /></>;
  }
  // Password temporanea appena consegnata: si sceglie la propria prima di lavorare.
  // Non vale per un accesso di assistenza: quella password la deve scegliere il cliente, e
  // l'amministratore non puo cambiarla. Chiedergliela lo bloccherebbe su una schermata che
  // non ha nessun modo di superare, proprio in una prova appena creata.
  if (session.data.user.mustChangePassword && !session.data.impersonation) {
    return <>{avvisoAssistenza}<ProfilePage session={session.data} forzato /></>;
  }

  const canManage = session.data.membership.role !== "collaborator";
  return (
    <>
    {avvisoAssistenza}
    <OfflineOutboxBanner
      companyId={session.data.company.id}
      userId={session.data.user.id}
      onSynced={() => { void utils.invalidate(); }}
    />
    <AppShell session={session.data} onLogout={() => logout.mutate()}>
      <ScrollToTop />
      <OwnerAnnouncement />
      <Routes>
        <Route path="/" element={<TodayPage session={session.data} />} />
        <Route path="/settimana" element={<WeekPage session={session.data} />} />
        <Route path="/interventi" element={<InterventionsPage session={session.data} />} />
        <Route path="/interventi/:id" element={<InterventionPage session={session.data} />} />
        <Route path="/strutture" element={<SitesPage session={session.data} />} />
        <Route path="/strutture/:id" element={<SitePage />} />
        <Route path="/registro-ore" element={<WorkLogPage session={session.data} />} />
        <Route path="/piscine" element={<PoolsPage session={session.data} />} />
        <Route path="/acquisti" element={<PurchasingLayout session={session.data} />}>
          <Route index element={<Navigate to="richieste" replace />} />
          <Route path="richieste" element={<PurchaseRequestsPage session={session.data} />} />
          <Route path="fornitori" element={canManage ? <SuppliersPage /> : <Navigate to="/acquisti/richieste" replace />} />
          <Route path="confronto" element={canManage ? <PriceComparisonPage /> : <Navigate to="/acquisti/richieste" replace />} />
          <Route path="ordini" element={canManage ? <PurchaseOrdersPage /> : <Navigate to="/acquisti/richieste" replace />} />
          <Route path="ordini/:id" element={canManage ? <PurchaseOrderPage /> : <Navigate to="/acquisti/richieste" replace />} />
          <Route path="riepilogo" element={canManage ? <PurchaseReportPage /> : <Navigate to="/acquisti/richieste" replace />} />
        </Route>
        <Route path="/squadra" element={canManage ? <TeamPage /> : <Navigate to="/" replace />} />
        <Route path="/catalogo" element={canManage ? <CatalogPage /> : <Navigate to="/" replace />} />
        <Route path="/profilo" element={<ProfilePage session={session.data} />} />
        <Route path="/comunicazioni" element={<ChatPage session={session.data} />} />
        <Route path="/supporto" element={<SupportPage session={session.data} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
    </>
  );
}
